import React, { useEffect } from "react";
import "./legalPages.css";

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = "Privacy Policy - FNB Kitchen Mobile App";
    return () => {
      document.title = "FBK ADMIN";
    };
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: February 10, 2026</p>

        <div className="section">
          <h2>1. Introduction</h2>
          <p>
            FNB Kitchen Mobile App ("we," "us," "our") provides a field service management application for technicians and customers. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application. By using the FNB Kitchen Mobile App app, you consent to the data practices described in this policy.
          </p>
        </div>

        <div className="section">
          <h2>2. Information We Collect</h2>

          <h3>2.1 Personal Information</h3>
          <p>We collect personal information that you provide directly to us, including:</p>
          <ul>
            <li>Name, email address, password, and phone number (for account registration)</li>
            <li>Profile images</li>
            <li>Digital signatures (for service reports)</li>
            <li>Billing and payment information</li>
            <li>Service reports, work orders, and job-related information</li>
          </ul>

          <h3>2.2 Device Information</h3>
          <p>We automatically collect certain device information when you use our app:</p>
          <ul>
            <li>Device type, model, and operating system</li>
            <li>Device identifiers (Device ID)</li>
            <li>Firebase Cloud Messaging (FCM) token for push notifications</li>
            <li>App version and usage data</li>
          </ul>

          <h3>2.3 Media Content</h3>
          <p>When you create service reports, we collect:</p>
          <ul>
            <li>Photos and videos captured through the app (maximum 5 minutes for videos)</li>
            <li>Associated metadata (EXIF data, timestamps)</li>
            <li>Audio recordings (when recording videos with audio)</li>
          </ul>

          <h3>2.4 Information We DO NOT Collect</h3>
          <p>We explicitly do NOT collect:</p>
          <ul>
            <li>GPS location data or precise geolocation</li>
            <li>Browsing history</li>
            <li>Contacts or address book</li>
            <li>Social media information</li>
            <li>Biometric data (except service-related digital signatures)</li>
            <li>Analytics or behavioral tracking data</li>
          </ul>
        </div>

        <div className="section">
          <h2>3. How We Use Your Information</h2>
          <p>We use the collected information to:</p>
          <ul>
            <li>Provide, operate, and maintain our field service management services</li>
            <li>Manage user accounts and authentication</li>
            <li>Process and fulfill service requests and work orders</li>
            <li>Send push notifications for job assignments, updates, and reminders</li>
            <li>Generate service reports and billing documentation</li>
            <li>Communicate with you about services, updates, and support</li>
            <li>Improve app functionality and user experience</li>
            <li>Comply with legal obligations and resolve disputes</li>
          </ul>
        </div>

        <div className="section">
          <h2>4. Data Storage and Security</h2>

          <h3>4.1 Local Storage</h3>
          <p>
            We store authentication tokens and user state locally on your device using AsyncStorage (for authentication tokens) and Redux-persist (for app state management). This data remains on your device and is used to maintain your logged-in session.
          </p>

          <h3>4.2 Remote Storage</h3>
          <p>
            Your personal data, service reports, and media files are transmitted securely via HTTPS to our servers. We implement industry-standard security measures including encryption in transit and at rest.
          </p>

          <h3>4.3 Security Measures</h3>
          <p>
            We implement administrative, technical, and physical security measures to protect your information. However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your data, we cannot guarantee absolute security.
          </p>
        </div>

        <div className="section">
          <h2>5. Data Sharing and Disclosure</h2>

          <h3>5.1 Third-Party Services</h3>
          <p>
            We use Firebase Cloud Messaging (FCM) as our ONLY third-party service for delivering push notifications. FCM processes device tokens and notification content to deliver messages to your device. We do NOT use any analytics services, crash reporting tools, or advertising platforms.
          </p>

          <h3>5.2 Service-Related Sharing</h3>
          <p>
            Service reports, photos, videos, and signatures may be shared with relevant parties (customers, supervisors, or administrators) as necessary to fulfill service requests and maintain business records.
          </p>

          <h3>5.3 Legal Requirements</h3>
          <p>
            We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., court orders, subpoenas, law enforcement).
          </p>
        </div>

        <div className="section">
          <h2>6. Data Retention</h2>
          <ul>
            <li><strong>Active accounts:</strong> We retain your personal data and service records for as long as your account is active or as needed to provide services. Service reports and related documentation are retained for 7 years to comply with business and legal requirements.</li>
            <li><strong>Deleted accounts:</strong> When you request account deletion, your personal data will be permanently deleted within 30 days, except where retention is required by law.</li>
            <li><strong>Local data:</strong> Data stored locally on your device is deleted when you uninstall the app or clear app data.</li>
          </ul>
        </div>

        <div className="section">
          <h2>7. Your Rights and Choices</h2>
          <p>You have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
            <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements)</li>
            <li><strong>Restriction:</strong> Request restriction of processing your personal data</li>
            <li><strong>Portability:</strong> Request transfer of your data to another service (where technically feasible)</li>
            <li><strong>Notification preferences:</strong> Disable push notifications through device settings</li>
          </ul>
          <p>To exercise these rights, contact us at <a href="mailto:info@fbk.com.sg">info@fbk.com.sg</a>.</p>
        </div>

        <div className="section">
          <h2>8. Permissions Required</h2>
          <p>The app requires the following device permissions:</p>
          <ul>
            <li><strong>Camera:</strong> To capture photos and videos for service reports</li>
            <li><strong>Photo Library:</strong> To access and upload media from your device</li>
            <li><strong>Microphone:</strong> To record audio when capturing videos</li>
            <li><strong>Notifications:</strong> To receive job assignments, updates, and reminders</li>
            <li><strong>Storage:</strong> To save and access media files</li>
            <li><strong>Internet:</strong> To communicate with our servers</li>
          </ul>
          <p><strong>Note:</strong> The app declares location permission in iOS but does NOT collect or use location data.</p>
        </div>

        <div className="section">
          <h2>9. Children&apos;s Privacy</h2>
          <p>Our app is intended for users aged 18 and older. We do not knowingly collect personal information from children under 18. If you believe we have inadvertently collected information from a child, please contact us immediately.</p>
        </div>

        <div className="section">
          <h2>10. International Data Transfers</h2>
          <p>Your information may be transferred to and processed on servers located outside your country of residence. By using our app, you consent to the transfer of your information to countries that may have different data protection laws. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.</p>
        </div>

        <div className="section">
          <h2>11. California Privacy Rights (CCPA)</h2>
          <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):</p>
          <ul>
            <li><strong>Right to Know:</strong> Request disclosure of personal information collected, used, or shared</li>
            <li><strong>Right to Delete:</strong> Request deletion of personal information we hold</li>
            <li><strong>Right to Opt-Out:</strong> Opt-out of the sale of personal information (Note: we do NOT sell personal information)</li>
            <li><strong>Right to Non-Discrimination:</strong> Exercise your privacy rights without discriminatory treatment</li>
          </ul>
          <p>To exercise these rights, contact us at <a href="mailto:info@fbk.com.sg">info@fbk.com.sg</a>.</p>
        </div>

        <div className="section">
          <h2>12. European Union Privacy Rights (GDPR)</h2>
          <p>If you are in the European Economic Area (EEA), you have rights under the General Data Protection Regulation (GDPR):</p>
          <ul>
            <li><strong>Legal Basis:</strong> We process your data based on contract performance, legitimate interests, and legal obligations</li>
            <li><strong>Right to Access:</strong> Obtain confirmation and access to your personal data</li>
            <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
            <li><strong>Right to Erasure:</strong> Request deletion (&quot;right to be forgotten&quot;)</li>
            <li><strong>Right to Restrict Processing:</strong> Request limitation of data processing</li>
            <li><strong>Right to Data Portability:</strong> Receive your data in a structured format</li>
            <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
            <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time</li>
            <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local supervisory authority</li>
          </ul>
          <p>To exercise these rights, contact our Data Protection Officer at <a href="mailto:info@fbk.com.sg">info@fbk.com.sg</a>.</p>
        </div>

        <div className="section">
          <h2>13. Changes to This Privacy Policy</h2>
          <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy in the app and updating the &quot;Last Updated&quot; date. Continued use of the app after changes become effective constitutes acceptance of the revised policy.</p>
        </div>

        <div className="section">
          <h2>14. Contact Information</h2>
          <p>If you have questions about this Privacy Policy or our data practices, contact us:</p>
          <div className="contact-info">
            <p><strong>Email:</strong> <a href="mailto:info@fbk.com.sg">info@fbk.com.sg</a></p>
            <p><strong>Phone:</strong> <a href="tel:+6591258874">+65 9125 8874</a></p>
            <p><strong>Address:</strong> 3 Ubi Ave 3, Crocodile house #03-04 Singapore 408857</p>
            <p><strong>Data Protection Officer:</strong> <a href="mailto:info@fbk.com.sg">info@fbk.com.sg</a></p>
          </div>
        </div>

        <div className="section">
          <h2>15. Consent</h2>
          <div className="consent-box">
            <p><strong>By using FNB Kitchen Mobile App, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with this policy, please do not use our app.</strong></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
